import React from 'react';
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/modern/components/ui/dropdown-menu';
import { TaskColorPicker } from './TaskColorPicker';
import { TaskStylePicker } from './TaskStylePicker';
import {
  canArchive,
  canDelete,
  canShare,
  canCopyToBoard,
  canExportHistory,
  canAdjustPoints,
} from '../contextMenuUtils';
import { showUnlinkRelatedEntityPrompt, openNewSpotIncentiveEditor, copyTextToClipboard } from '@/other/utils/utils';
import { Button } from '@/modern/components/ui/button';
import { DateHelper, Model } from '@bryntum/schedulerpro';
import { Task } from '@/bryntum-lib/models/Task';
import { toast } from 'sonner';
import zipcelx from 'zipcelx';
import { useConfirm } from '@omit/react-confirm-dialog';
import { useSwivuBryntumContext } from '@/react/hooks/useSwivuBryntumContext';

interface TaskContextMenuProps {
  record: Task;
  onClose?: () => void;
  onItemClick?: (item: { name: string; }) => void;
}

const TaskContextMenu: React.FC<TaskContextMenuProps> = ({ record, onClose, onItemClick }) => {
  const context = useSwivuBryntumContext();
  const { user, project, selectedBoard, boardManager, editorHandler, viewManager } = context;
  const confirm = useConfirm();

  const isDemoUser = user?.isDemoUser || false;

  // Permission checks
  const showArchive = canArchive(user, record);
  const showDelete = canDelete(user, record);
  const showShare = canShare(user, isDemoUser, record);
  const showCopyTo = canCopyToBoard(user, isDemoUser, selectedBoard, boardManager?.boardNameStore);
  const showExportHistory = canExportHistory(user, record);
  const showAdjustPoints = canAdjustPoints(user, project, record);
  const showColorPicker = !record?.isPhantom;
  const showStylePicker = !record?.isPhantom;

  // Action handlers
  const handleArchive = async () => {
    if (!record.archived && record.swivuUnlinkPromptConfig) {
      const isArchived = await showUnlinkRelatedEntityPrompt(record.swivuUnlinkPromptConfig);
      if (isArchived) {
        onClose?.();
      }
    } else {
      record.archived = !record.archived;
      onClose?.();
    }
  };

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: 'Delete task',
      description: `Are you sure you want to delete "${record?.name || 'this task'}"? This action cannot be undone.`,
      confirmButton: {
        variant: 'destructive',
        title: 'Delete task'
      },
      cancelButton: {
        variant: 'ghost'
      }
    });

    if (!confirmed) return;

    try {
      const recordStore = project?.extraTaskStore;
      if (recordStore) {
        recordStore.remove(record as unknown as Model);
        toast.success(`Task "${record?.name}" deleted successfully`);
        onClose?.();

        if (viewManager) {
          viewManager.goToPreviousView();
        }
      }
    } catch (error) {
      console.error('Failed to delete task:', error);
      toast.error('Something went wrong! Unable to delete task');
    }
  };

  const handleShare = async () => {
    await confirm({
      title: 'Share',
      description: 'How would you like to share?',
      customActions: ({ confirm }: { confirm: () => void }) => (
        <div className="flex gap-2">
          <Button
            onClick={() => {
              copyTextToClipboard(` ${record.name} \n ${window.location.href}`, "");
              confirm();
            }}
          >
            Description with URL
          </Button>
          <Button
            onClick={() => {
              copyTextToClipboard(window.location.href, "");
              confirm();
            }}
          >
            URL Only
          </Button>
        </div>
      )
    });
    onClose?.();
  };

  const handleCopyTo = () => {
    onItemClick?.({ name: 'copyTo' });
  };

  const handleExportHistory = () => {
    try {
      // Get the task's schedule events
      const events = record?.events || [];

      if (events.length === 0) {
        toast.warning('No scheduling data to export');
        onClose?.();
        return;
      }

      // Prepare data for Excel export
      const config = {
        filename: `Task_History_${record?.name || 'Export'}`,
        sheet: {
          data: [
            // Header row
            [
              { value: 'Resource', type: 'string' as const },
              { value: 'Description', type: 'string' as const },
              { value: 'Start Date', type: 'string' as const },
              { value: 'Start Time', type: 'string' as const },
              { value: 'End Date', type: 'string' as const },
              { value: 'End Time', type: 'string' as const },
              { value: 'Duration', type: 'string' as const },
            ],
            // Data rows
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ...events.map((event: any) => {
              const startDate = event.startDate ? new Date(event.startDate) : null;
              const endDate = event.endDate ? new Date(event.endDate) : null;

              // Calculate duration in milliseconds
              let duration = 'N/A';
              if (startDate && endDate) {
                const deltaMs = endDate.getTime() - startDate.getTime();
                duration = DateHelper.formatDelta(deltaMs, { abbrev: true }) as string;
              }

              return [
                { value: event.resource?.name || 'N/A', type: 'string' as const },
                { value: event.resourceDescription || 'N/A', type: 'string' as const },
                { value: startDate ? DateHelper.format(startDate, 'YYYY-MM-DD') : 'N/A', type: 'string' as const },
                { value: startDate ? DateHelper.format(startDate, 'HH:mm') : 'N/A', type: 'string' as const },
                { value: endDate ? DateHelper.format(endDate, 'YYYY-MM-DD') : 'N/A', type: 'string' as const },
                { value: endDate ? DateHelper.format(endDate, 'HH:mm') : 'N/A', type: 'string' as const },
                { value: duration, type: 'string' as const },
              ];
            }),
          ],
        },
      };

      // Export to Excel
      zipcelx(config);

      toast.success('Task history exported successfully');

      onClose?.();
    } catch (error) {
      console.error('Error exporting task history:', error);
      toast.error('Failed to export task history');
    }
  };

  const handleAdjustPoints = () => {
    openNewSpotIncentiveEditor({
      project,
      editorHandler,
      otherParams: { selectedTask: record },
    });
    onClose?.();
  };

  return (
    <>
      {/* Task Color Picker */}
      <TaskColorPicker record={record} disabled={!showColorPicker} onColorChange={() => {onClose?.()}} />

      {/* Task Style Picker */}
      <TaskStylePicker record={record} disabled={!showStylePicker} onStyleChange={() => {onClose?.()}} />

      {/* Separator */}
      <DropdownMenuSeparator />

      {/* Share */}
      <DropdownMenuItem onClick={handleShare} disabled={!showShare}>
        <i className="fa-light fa-share w-4 h-4" />
        <span>Share</span>
      </DropdownMenuItem>

      {/* Copy to */}
      <DropdownMenuItem onClick={handleCopyTo} disabled={!showCopyTo}>
        <i className="fa-light fa-copy w-4 h-4" />
        <span>Copy to</span>
      </DropdownMenuItem>

      {/* Export History */}
      <DropdownMenuItem onClick={handleExportHistory} disabled={!showExportHistory}>
        <i className="fa-light fa-file-export w-4 h-4" />
        <span>Export Task History</span>
      </DropdownMenuItem>

      {/* Adjust Points */}
      <DropdownMenuItem onClick={handleAdjustPoints} disabled={!showAdjustPoints}>
        <i className="fa-light fa-star w-4 h-4" />
        <span>Adjust Points</span>
      </DropdownMenuItem>

      {/* Separator before Archive/Delete */}
      <DropdownMenuSeparator />

      {/* Archive/Unarchive */}
      <DropdownMenuItem onClick={handleArchive} disabled={!showArchive}>
        <i className="fa-light fa-folder-open w-4 h-4" />
        <span>{record?.archived ? 'Unarchive' : 'Archive'}</span>
      </DropdownMenuItem>

      {/* Delete */}
      <DropdownMenuItem onClick={handleDelete} className="text-destructive" disabled={!showDelete}>
        <i className="fa-light fa-trash w-4 h-4" />
        <span>Delete</span>
      </DropdownMenuItem>
    </>
  );
};

export default TaskContextMenu;
